/**
 * Platform-agnostic storage adapter for Zustand persist middleware.
 *
 * Mobile: Call setStorageAdapter(AsyncStorage) in App.tsx before any stores init.
 * Web:    Call setStorageAdapter(localStorage) in _app.tsx / layout.tsx.
 *
 * All Zustand stores that use persist should import getStorageAdapter() from here
 * instead of directly importing AsyncStorage.
 */

import type { StateStorage } from "zustand/middleware";

let _adapter: StateStorage | null = null;

/**
 * Set the storage backend. Must be called once at app startup
 * before any Zustand store initializes.
 */
export function setStorageAdapter(adapter: StateStorage): void {
  _adapter = adapter;
}

/**
 * Get the storage adapter for use in createJSONStorage().
 * Falls back to a no-op in-memory store if not yet set (SSR safety).
 */
export function getStorageAdapter(): StateStorage {
  if (_adapter) return _adapter;

  // Fallback: in-memory storage (safe for SSR / tests)
  console.warn("[storageAdapter] No adapter set — using in-memory fallback");
  const mem = new Map<string, string>();
  return {
    getItem: (key: string) => mem.get(key) ?? null,
    setItem: (key: string, value: string) => { mem.set(key, value); },
    removeItem: (key: string) => { mem.delete(key); },
  };
}
